import { useMemo } from 'react';

import type {
	AcceptanceTimeItem,
	ResponseRatioItem,
	TotalActionsPerOrganizationItem,
	TotalActionsRowItem,
	TriggeredItem,
} from '@/app/types/Dashboard';
import type { Filter, MessageContainerFilter } from '@/app/types/Filter';
import type { ColoredOrganization } from '@/features/Shared/model/types/organization.dto';

import { useOrganizationColors } from '@/app/context/OrganizationsColorProvider/ColorProviderContext';
import { resolveColorForItems } from '@/app/utils/colors';
import { roundPercentagesToTotal } from '@/app/utils/number';
import { useActionDashboardQuery } from '@/features/Dashboard/api';
import { useOrganizations } from '@/features/Shared/hooks/useOrganizations';

export type UseActionDashboardQueryData = ReturnType<typeof useActionDashboardQueryData>;

export const useDashboardOrganizations = () => {
	const { organizations } = useOrganizations();
	const colors = useOrganizationColors();

	return useMemo(() => {
		return organizations.map((org) => ({
			...org,
			color: colors[org.id] || '#000000',
		})) as ColoredOrganization[];
	}, [colors, organizations]);
};

export const useSelectedOrganization = (filter: MessageContainerFilter) => {
	const organizations = useDashboardOrganizations();
	return useMemo(() => {
		if (!filter.organizationIds || filter.organizationIds.length === 0) {
			return organizations;
		}

		return organizations.filter((org) => filter.organizationIds?.includes(org.id) ?? false);
	}, [filter.organizationIds, organizations]);
};

export const useActionDashboardQueryData = (filter: Filter) => {
	const organizations = useDashboardOrganizations();
	const {
		data: dashboard,
		isFetching,
		isLoading,
		error,
		refetch,
	} = useActionDashboardQuery(filter);

	const result = {
		refetch,
		error: undefined,
		data: undefined,
	};

	if (isFetching || isLoading) {
		return {
			...result,
			isLoading: true,
		};
	}

	if (error) {
		return {
			...result,
			error,
			isLoading: false,
		};
	}

	const { summary, triggered } = dashboard ?? {};

	const organizationById: Record<string, ColoredOrganization> = organizations.reduce(
		(acc: Record<string, ColoredOrganization>, org: ColoredOrganization) => {
			acc[org.id] = org;
			return acc;
		},
		{}
	);

	const summeryGroupedByOrgId: Record<string, typeof summary> = (summary ?? []).reduce(
		(acc: Record<string, typeof summary>, item) => {
			const orgId = item.organizationId ?? '';
			acc[orgId] ??= [];
			acc[orgId].push(item);
			return acc;
		},
		{}
	);

	const triggersByOrgId: Record<string, TriggeredItem[]> = (triggered ?? []).reduce(
		(acc: Record<string, TriggeredItem[]>, trigger: TriggeredItem) => {
			const orgId = trigger.organizationId ?? '';
			if (!acc[orgId]) {
				acc[orgId] = [];
			}
			acc[orgId].push(trigger);
			return acc;
		},
		{}
	);

	const groupedTriggerItems = new Map<string, TriggeredItem>(
		triggered?.filter((item) => item.total > 0)?.map((item) => [item.triggerId, item])
	);

	const triggersWithColors = resolveColorForItems(groupedTriggerItems.values().toArray());

	const totalActionsRows: TotalActionsRowItem[] = triggersWithColors.map((item) => {
		const organizationId = item.organizationId ?? '';
		const organizationName = organizationById[organizationId]?.name || '';
		const totalForOrganization = summeryGroupedByOrgId[organizationId]?.[0]?.total ?? 1;

		const rounded = roundPercentagesToTotal([
			((item.accepted ?? 0) / totalForOrganization) * 100,
			((item.rejected ?? 0) / totalForOrganization) * 100,
			((item.unanswered ?? 0) / totalForOrganization) * 100,
		]);

		return {
			organizationId: item.organizationId ?? '',
			organizationName,
			organizationColor: organizationById[organizationId]?.color || '',
			triggerColor: item.color,
			triggerName: item.trigger || '',
			total: ((item.total ?? 1) / totalForOrganization) * 100,
			totalActions: item.total ?? 0,
			acceptedActions: rounded[0],
			declinedActions: rounded[1],
			noResponseActions: rounded[2],
			acceptTime: item.answeredTimeAverageInMs
				? Math.round(item.answeredTimeAverageInMs / 1000)
				: 0,
		};
	});

	const totalActionsSeries: TotalActionsPerOrganizationItem[] = (summary ?? []).map((item) => {
		const organizationId = item.organizationId ?? '';
		const organizationName = organizationById[organizationId]?.name || '';
		const triggerData = triggersByOrgId[organizationId] || [];

		return {
			organizationName,
			organizationId: item.organizationId ?? '',
			organizationTotal: item.total ?? 0,
			color: organizationById[organizationId]?.color || '',
			triggeredData:
				triggerData.map((trigger) => {
					return {
						isExternal: trigger.isExternal,
						trigger: trigger.trigger,
						triggerGroup: trigger.triggerGroup,
						percentage: ((trigger.total / (item.total ?? 1)) * 100).toFixed(0),
					};
				}) || [],
		};
	});

	const acceptedTimeSeries: AcceptanceTimeItem[] = (summary ?? []).map((item) => {
		const organizationId = item.organizationId ?? '';
		const organizationName = organizationById[organizationId]?.name || '';
		const averageTime =
			item.acceptedTimeAverageInMs != null
				? Math.round(item.acceptedTimeAverageInMs / 1000)
				: 0;
		return {
			organizationId: item.organizationId ?? '',
			averageTime,
			color: organizationById[organizationId]?.color || '',
			organizationName,
		};
	});

	const responseRationSeries: ResponseRatioItem[] = (summary ?? []).map((item) => {
		const organizationId = item.organizationId ?? '';
		const organizationName = organizationById[organizationId]?.name || '';
		return {
			organizationId,
			organizationName,
			accepted: item.accepted,
			rejected: item.rejected,
			unanswered: item.unanswered,
			total: item.total,
		};
	});

	return {
		...result,
		isLoading: false,
		data: {
			totalActionsSeries,
			acceptedTimeSeries,
			responseRationSeries,
			totalActionsRows,
		},
	};
};
